// BadgeCase — grid of earned + locked badges, tooltip on hover explains how
// to unlock each one. Used on the owner's Catalogue page and public profiles.

import {
  Layers, Medal, Clock, Hourglass, Compass, Bot, Heart, Skull, Trophy,
  Flame, Star, ClipboardList, Check, Radio, Disc, Album, Sparkles, Award,
} from "lucide-react";

const ICONS = {
  layers: Layers,
  medal: Medal,
  clock: Clock,
  hourglass: Hourglass,
  portal: Compass,
  robot: Bot,
  heart: Heart,
  skull: Skull,
  trophy: Trophy,
  flame: Flame,
  star: Star,
  clipboard: ClipboardList,
  check: Check,
  broadcast: Radio,
  vhs: Disc,
  tape: Album,
  spark: Sparkles,
  default: Award,
};

const CATEGORY_LABEL = {
  volume: "Volume",
  genre: "Genre",
  behavior: "Behavior",
  seasonal: "Seasonal",
};

function formatEarned(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return null;
  }
}

export default function BadgeCase({ catalog, earned, testid = "badge-case" }) {
  if (!catalog?.length) return null;
  const earnedMap = new Map((earned || []).map((e) => [e.badge_key, e.earned_at]));
  const earnedCount = earnedMap.size;

  return (
    <section className="mt-14" data-testid={testid}>
      <div className="flex items-baseline justify-between border-b border-white/10 pb-3">
        <div className="flex items-center gap-3">
          <Award className="h-5 w-5 text-[#FF3B30]" />
          <h2 className="font-display text-3xl uppercase tracking-widest text-white">Badges</h2>
        </div>
        <span
          data-testid={`${testid}-count`}
          className="font-mono text-[11px] uppercase tracking-widest text-neutral-500"
        >
          {earnedCount} / {catalog.length} earned
        </span>
      </div>

      <div
        data-testid={`${testid}-grid`}
        className="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8"
      >
        {catalog.map((b) => (
          <BadgeTile
            key={b.key}
            badge={b}
            earnedAt={earnedMap.get(b.key)}
          />
        ))}
      </div>
    </section>
  );
}

function BadgeTile({ badge, earnedAt }) {
  const Icon = ICONS[badge.icon] || ICONS.default;
  const isEarned = !!earnedAt;
  const earnedLabel = formatEarned(earnedAt);
  const catLabel = CATEGORY_LABEL[badge.category] || badge.category;

  return (
    <div
      data-testid={`badge-tile-${badge.key}`}
      className="group relative flex flex-col items-center"
      // Native title attribute is the accessibility fallback for touch users.
      title={`${badge.name} · ${badge.description}`}
    >
      <div
        className={`relative flex aspect-square w-full items-center justify-center border transition-all ${
          isEarned
            ? "border-[#FF3B30] bg-gradient-to-br from-[#FF3B30]/25 via-[#FF3B30]/10 to-transparent"
            : "border-white/10 bg-neutral-950 grayscale"
        }`}
      >
        <Icon
          className={`h-8 w-8 md:h-10 md:w-10 ${isEarned ? "text-[#FF3B30]" : "text-neutral-600"}`}
        />
        {!isEarned && (
          <span className="absolute inset-0 bg-black/30" aria-hidden="true" />
        )}
      </div>
      <p
        className={`mt-2 line-clamp-1 text-center font-display text-sm uppercase tracking-wide ${
          isEarned ? "text-white" : "text-neutral-500"
        }`}
      >
        {badge.name}
      </p>

      {/* Progress bar — shown only for locked, countable badges. Boolean
          badges (target ≤ 1) skip the bar because "0/1" reads as noise. */}
      {!isEarned && badge.progress && badge.progress.target > 1 && (
        <div className="mt-1 w-full" data-testid={`badge-progress-${badge.key}`}>
          <div className="flex items-baseline justify-between font-mono text-[9px] uppercase tracking-widest text-neutral-500">
            <span>{badge.progress.current}</span>
            <span>/ {badge.progress.target}</span>
          </div>
          <div className="mt-1 h-1 w-full bg-white/5">
            <div
              className="h-full bg-[#FF3B30]/60 transition-all"
              style={{ width: `${Math.min(100, (badge.progress.current / badge.progress.target) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Hover tooltip — desktop only. Falls back to the `title` attribute on mobile/touch. */}
      <div className="pointer-events-none absolute -top-2 left-1/2 z-20 hidden w-56 -translate-x-1/2 -translate-y-full border border-white/15 bg-black p-3 opacity-0 shadow-xl transition-opacity group-hover:opacity-100 md:block">
        <p className="font-mono text-[9px] uppercase tracking-widest text-[#FF3B30]">
          {catLabel} {isEarned ? "· earned" : "· locked"}
        </p>
        <p className="mt-1 font-display text-base uppercase leading-tight tracking-wide text-white">
          {badge.name}
        </p>
        <p className="mt-1.5 text-xs leading-snug text-neutral-300">{badge.description}</p>
        {!isEarned && badge.progress && badge.progress.target > 1 && (
          <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">
            {badge.progress.current} / {badge.progress.target}{badge.progress.unit ? ` ${badge.progress.unit}` : ""}
          </p>
        )}
        {isEarned && earnedLabel && (
          <p className="mt-2 font-mono text-[9px] uppercase tracking-widest text-neutral-500">
            Unlocked {earnedLabel}
          </p>
        )}
      </div>
    </div>
  );
}
